'use client'

import { Carousel, CarouselItem } from "@/components/elements/Carousel"
import { formatImageURL, formatImageURLGoogle, getActiveBanners } from "@/utils/util"
import { navigateUrl } from "@/utils/client-request"
import { usePathname } from "next/navigation"
import { useSession } from "next-auth/react"
import Image from "next/image"

const RATIO_CLASS: Record<string, string> = {
  '9/4': 'aspect-4/5 sm:aspect-[9/4]',
  '16/9': 'aspect-4/5 sm:aspect-[16/9]',
  '4/3': 'aspect-4/5 sm:aspect-[4/3]',
  '21/9': 'aspect-4/5 sm:aspect-[21/9]',
  '3/2': 'aspect-4/5 sm:aspect-[3/2]',
}

type Campaign = {
  id: string
  name: string
  handle: string
  campaignBanner?: string
  customerOnly?: boolean
}

type Slide = { src: string, alt: string, link?: string }

export default function Hero({ vendor, activeCampaigns = [] }: { vendor: any, activeCampaigns?: Campaign[] }) {
  const pathname = usePathname()
  const { data: session } = useSession()

  const liveVendorBanners = getActiveBanners(vendor?.banners || [])

  const liveCampaigns = activeCampaigns.filter((c) => c.campaignBanner && (!c.customerOnly || !!session?.user?.id))

  const campaignSlides: Slide[] = liveCampaigns.map((c) => ({
    src: formatImageURLGoogle(c.campaignBanner!),
    alt: c.name,
    link: navigateUrl(`/c/${c.handle}`, pathname),
  }))

  const bannerSlides: Slide[] = liveVendorBanners.map((b: any, i: number) => ({
    src: formatImageURL(b.image),
    alt: `Banner ${i + 1}`,
    link: b.link && b.link.trim() !== '' ? b.link : undefined,
  }))

  const slides = [...campaignSlides, ...bannerSlides]
  if (slides.length === 0) return null

  const ratio = vendor?.bannerAspectRatio && RATIO_CLASS[vendor.bannerAspectRatio] ? vendor.bannerAspectRatio : '9/4'
  const ratioClass = RATIO_CLASS[ratio]
  const fullWidth = !!vendor?.bannerFullWidth

  const wrapperClass = fullWidth ? 'w-full' : 'cont pt-6 pb-8'
  const itemRoundedClass = fullWidth ? '' : 'rounded-2xl sm:rounded-3xl'
  const itemBorderStyle = fullWidth ? undefined : { border: '1px solid rgba(255,255,255,0.13)' }

  return (
    <div className={wrapperClass}>
      <Carousel className="max-w-none! cursor-grab active:cursor-grabbing" autoPlay delay={5000}>
        {slides.map((slide, index) => {
          const inner = (
            <div
              className={`relative w-full overflow-hidden select-none ${ratioClass} ${itemRoundedClass}`}
              style={itemBorderStyle}
            >
              <Image
                loader={myLoader}
                src={slide.src}
                fill
                draggable={false}
                sizes={fullWidth ? '100vw' : '(min-width: 1280px) 1280px, 100vw'}
                className="object-cover"
                alt={slide.alt}
                priority={index === 0}
              />
              <div
                className="pointer-events-none absolute inset-0"
                style={{ background: 'radial-gradient(50% 70% at 80% 30%, rgba(255,255,255,.06), transparent 60%)' }}
              />
              <div
                className="pointer-events-none absolute inset-0"
                style={{ background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.35) 100%)' }}
              />
            </div>
          )
          return (
            <CarouselItem key={`Slide-${index}`}>
              {slide.link ? (
                <a href={slide.link} aria-label={slide.alt} draggable={false} className="block">
                  {inner}
                </a>
              ) : (
                inner
              )}
            </CarouselItem>
          )
        })}
      </Carousel>
    </div>
  )
}

const myLoader = ({ src, width, quality }: any) => {
  return `${src}?w=${width}&q=${quality || 75}`
}
